import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import {
  HiOutlineSearch, HiOutlineCloud, HiOutlineUserGroup, HiOutlineCube,
  HiOutlineGlobe, HiOutlineServer, HiOutlineCog, HiOutlineViewList,
  HiOutlineLightningBolt, HiOutlineClock, HiOutlineDatabase, HiOutlineKey,
  HiOutlineChip, HiOutlineMail, HiOutlineChevronDown, HiOutlineRefresh,
} from 'react-icons/hi';
import {
  getCloudRunServicesList, getAllVMInstances, getCloudRunJobs,
  getTaskQueues, getCloudBuildTriggersLight, getSchedulerJobs,
  getBuckets, getServiceAccounts, getPubSubTopics,
} from '../services/api';
import selectStyles from '../shared/selectStyles';
import './Search.css';

const SERVICE_TYPES = [
  { value: 'cloud_run', label: 'Cloud Run Service' },
  { value: 'vm_instance', label: 'VM Instance' },
  { value: 'cloud_run_job', label: 'Cloud Run Job' },
  { value: 'cloud_task', label: 'Cloud Task Queue' },
  { value: 'cloud_build', label: 'Cloud Build Trigger' },
  { value: 'cloud_scheduler', label: 'Cloud Scheduler Job' },
  { value: 'bucket', label: 'GCS Bucket' },
  { value: 'service_account', label: 'Service Account' },
  { value: 'gemini_api', label: 'Gemini API' },
  { value: 'pub_sub', label: 'Pub/Sub Topic' },
];

const TYPE_META = {
  cloud_run:        { label: 'Cloud Run Services',   icon: HiOutlineGlobe,          color: 'var(--accent-blue)' },
  vm_instance:      { label: 'VM Instances',         icon: HiOutlineServer,         color: 'var(--accent-green, #22c55e)' },
  cloud_run_job:    { label: 'Cloud Run Jobs',       icon: HiOutlineCog,            color: 'var(--accent-purple, #a855f7)' },
  cloud_task:       { label: 'Cloud Task Queues',    icon: HiOutlineViewList,       color: 'var(--accent-orange, #f97316)' },
  cloud_build:      { label: 'Cloud Build Triggers', icon: HiOutlineLightningBolt,  color: 'var(--accent-yellow, #eab308)' },
  cloud_scheduler:  { label: 'Cloud Scheduler Jobs', icon: HiOutlineClock,          color: 'var(--accent-teal, #14b8a6)' },
  bucket:           { label: 'GCS Buckets',          icon: HiOutlineDatabase,       color: 'var(--accent-pink, #ec4899)' },
  service_account:  { label: 'Service Accounts',     icon: HiOutlineKey,            color: 'var(--accent-indigo, #6366f1)' },
  gemini_api:       { label: 'Gemini API',           icon: HiOutlineChip,           color: 'var(--accent-rose, #f43f5e)' },
  pub_sub:          { label: 'Pub/Sub Topics',       icon: HiOutlineMail,           color: 'var(--accent-cyan, #06b6d4)' },
};

const LIVE_FETCHERS = {
  cloud_run:       (pid) => getCloudRunServicesList(pid).then(r => r.data),
  vm_instance:     (pid) => getAllVMInstances(pid).then(r => r.data),
  cloud_run_job:   (pid) => getCloudRunJobs(pid).then(r => r.data),
  cloud_task:      (pid) => getTaskQueues(pid).then(r => r.data),
  cloud_build:     (pid) => getCloudBuildTriggersLight(pid).then(r => r.data),
  cloud_scheduler: (pid) => getSchedulerJobs(pid).then(r => r.data),
  bucket:          (pid) => getBuckets(pid).then(r => r.data),
  service_account: (pid) => getServiceAccounts(pid).then(r => r.data),
  pub_sub:         (pid) => getPubSubTopics(pid).then(r => r.data),
};

const SERVICE_ROUTE_MAP = {
  cloud_run: '/cloud-run-services',
  vm_instance: '/vm-instances',
  cloud_run_job: '/cloud-run-jobs',
  cloud_task: '/cloud-tasks',
  cloud_build: '/cloud-build',
  cloud_scheduler: '/cloud-scheduler',
  bucket: '/vm-instances',
  service_account: '/vm-instances',
  pub_sub: '/vm-instances',
};

function Search({ gcpProjects, products, teamMembers }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  const [liveResources, setLiveResources] = useState({});
  const [liveLoading, setLiveLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const projectOptions = useMemo(() =>
    gcpProjects.map(p => ({ value: p._id, label: `${p.projectName} (${p.projectId})`, projectName: p.projectName, projectId: p.projectId })),
    [gcpProjects]
  );

  const memberOptions = useMemo(() =>
    teamMembers.map(m => ({ value: m._id, label: m.name })),
    [teamMembers]
  );

  const serviceTypeOptions = useMemo(() =>
    SERVICE_TYPES.map(t => ({ value: t.value, label: t.label })),
    []
  );

  const fetchLiveResources = useCallback(async (projectDbId) => {
    if (!projectDbId) { setLiveResources({}); return; }
    setLiveLoading(true);
    const results = {};
    const types = selectedServiceType ? [selectedServiceType.value] : Object.keys(LIVE_FETCHERS);

    const settled = await Promise.allSettled(
      types.filter(t => t !== 'gemini_api').map(async (type) => {
        const data = await LIVE_FETCHERS[type](projectDbId);
        return { type, data };
      })
    );

    for (const r of settled) {
      if (r.status === 'fulfilled') {
        results[r.value.type] = r.value.data || [];
      }
    }

    setLiveResources(results);
    setLiveLoading(false);
  }, [selectedServiceType]);

  useEffect(() => {
    if (selectedProject) {
      fetchLiveResources(selectedProject.value);
    } else {
      setLiveResources({});
    }
  }, [selectedProject, fetchLiveResources]);

  const ownerIndex = useMemo(() => {
    const idx = {};
    for (const m of teamMembers) {
      for (const svc of (m.services || [])) {
        const key = `${svc.type}::${svc.name}::${svc.gcpProjectId}`;
        if (!idx[key]) idx[key] = [];
        idx[key].push({ _id: m._id, name: m.name });
      }
    }
    return idx;
  }, [teamMembers]);

  const productIndex = useMemo(() => {
    const idx = {};
    for (const p of products) {
      for (const cr of (p.cloudRuns || [])) {
        const key = `cloud_run::${cr.serviceName}::${cr.gcpProjectId}`;
        if (!idx[key]) idx[key] = [];
        idx[key].push({ _id: p._id, name: p.name });
      }
    }
    return idx;
  }, [products]);

  const filteredResults = useMemo(() => {
    const q = search.toLowerCase().trim();
    const sections = {};

    const matchesQuery = (...fields) => {
      if (!q) return true;
      return fields.some(f => f && f.toLowerCase().includes(q));
    };

    if (!selectedServiceType && !selectedMember) {
      const matchedProjects = gcpProjects.filter(p =>
        matchesQuery(p.projectName, p.projectId) &&
        (!selectedProject || p._id === selectedProject.value)
      );
      if (matchedProjects.length) sections.gcp_projects = matchedProjects;
    }

    if (!selectedServiceType) {
      let matchedMembers = teamMembers;
      if (selectedMember) {
        matchedMembers = teamMembers.filter(m => m._id === selectedMember.value);
      }
      if (selectedProject) {
        matchedMembers = matchedMembers.filter(m =>
          m.services?.some(s => s.gcpProjectId === selectedProject.value)
        );
      }
      matchedMembers = matchedMembers.filter(m =>
        matchesQuery(m.name, ...(m.services || []).map(s => s.name))
      );
      if (matchedMembers.length) sections.team_members = matchedMembers;
    }

    if (!selectedServiceType && !selectedMember) {
      let matchedProducts = products;
      if (selectedProject) {
        matchedProducts = matchedProducts.filter(p =>
          p.cloudRuns?.some(cr => cr.gcpProjectId === selectedProject.value)
        );
      }
      matchedProducts = matchedProducts.filter(p =>
        matchesQuery(p.name, ...(p.cloudRuns || []).map(cr => cr.serviceName))
      );
      if (matchedProducts.length) sections.products = matchedProducts;
    }

    if (selectedMember) {
      const member = teamMembers.find(m => m._id === selectedMember.value);
      if (member) {
        let services = member.services || [];
        if (selectedProject) {
          services = services.filter(s => s.gcpProjectId === selectedProject.value);
        }
        if (selectedServiceType) {
          services = services.filter(s => s.type === selectedServiceType.value);
        }
        services = services.filter(s => matchesQuery(s.name, s.gcpProjectName, s.region, TYPE_META[s.type]?.label));

        const grouped = {};
        for (const s of services) {
          if (!grouped[s.type]) grouped[s.type] = [];
          grouped[s.type].push({ ...s, _source: 'member', _memberName: member.name, _memberId: member._id });
        }
        for (const [type, svcs] of Object.entries(grouped)) {
          sections[`service_${type}`] = svcs;
        }
      }
    } else if (selectedProject) {
      const allTeamServices = [];
      for (const m of teamMembers) {
        for (const s of (m.services || [])) {
          if (s.gcpProjectId === selectedProject.value) {
            allTeamServices.push({ ...s, _source: 'member', _memberName: m.name, _memberId: m._id });
          }
        }
      }

      for (const [type, resources] of Object.entries(liveResources)) {
        if (selectedServiceType && type !== selectedServiceType.value) continue;
        const merged = [];
        const teamSvcsOfType = allTeamServices.filter(s => s.type === type);
        const teamNames = new Set(teamSvcsOfType.map(s => s.name));

        for (const r of resources) {
          const name = r.name || r.email || r.id;
          if (!matchesQuery(name, r.region, r.zone, r.location, r.status)) continue;
          merged.push({
            type, name, region: r.region || r.zone || r.location || '',
            status: r.status || r.state || '',
            gcpProjectId: selectedProject.value,
            gcpProjectName: selectedProject.projectName,
            _source: teamNames.has(name) ? 'assigned' : 'live',
          });
        }

        for (const ts of teamSvcsOfType) {
          if (!merged.some(m => m.name === ts.name)) {
            if (matchesQuery(ts.name, ts.region, ts.gcpProjectName)) {
              merged.push(ts);
            }
          }
        }

        if (merged.length) sections[`service_${type}`] = merged;
      }

      if (!Object.keys(liveResources).length) {
        for (const s of allTeamServices) {
          if (selectedServiceType && s.type !== selectedServiceType.value) continue;
          if (!matchesQuery(s.name, s.region, s.gcpProjectName)) continue;
          const key = `service_${s.type}`;
          if (!sections[key]) sections[key] = [];
          sections[key].push(s);
        }
      }
    } else if (selectedServiceType) {
      const services = [];
      for (const m of teamMembers) {
        for (const s of (m.services || [])) {
          if (s.type === selectedServiceType.value && matchesQuery(s.name, s.gcpProjectName, s.region)) {
            services.push({ ...s, _source: 'member', _memberName: m.name, _memberId: m._id });
          }
        }
      }
      if (services.length) sections[`service_${selectedServiceType.value}`] = services;
    }

    return sections;
  }, [search, selectedProject, selectedServiceType, selectedMember, gcpProjects, products, teamMembers, liveResources]);

  const totalCount = useMemo(() =>
    Object.values(filteredResults).reduce((sum, arr) => sum + arr.length, 0),
    [filteredResults]
  );

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const isSectionExpanded = (key) => expandedSections[key] !== false;

  const getOwners = (type, name, projectId) => {
    const key = `${type}::${name}::${projectId}`;
    return ownerIndex[key] || [];
  };

  const getProducts = (name, projectId) => {
    const key = `cloud_run::${name}::${projectId}`;
    return productIndex[key] || [];
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedProject(null);
    setSelectedServiceType(null);
    setSelectedMember(null);
    setLiveResources({});
  };

  const hasFilters = search || selectedProject || selectedServiceType || selectedMember;

  const sectionOrder = [
    'gcp_projects', 'team_members', 'products',
    ...Object.keys(TYPE_META).map(t => `service_${t}`),
  ];

  const orderedSections = sectionOrder.filter(key => filteredResults[key]?.length);

  return (
    <div className="search-page">
      <div className="pv-header">
        <div className="pv-header-left">
          <h1><HiOutlineSearch /> Search</h1>
          <p className="pv-subtitle">Search across projects, services, team members, and products</p>
        </div>
      </div>

      <div className="search-filters">
        <div className="search-filter-row">
          <div className="search-filter-item">
            <label>Project</label>
            <Select
              styles={selectStyles}
              options={projectOptions}
              value={selectedProject}
              onChange={setSelectedProject}
              placeholder="All Projects"
              isClearable
              menuPortalTarget={document.body}
            />
          </div>
          <div className="search-filter-item">
            <label>Service Type</label>
            <Select
              styles={selectStyles}
              options={serviceTypeOptions}
              value={selectedServiceType}
              onChange={setSelectedServiceType}
              placeholder="All Services"
              isClearable
              menuPortalTarget={document.body}
            />
          </div>
          <div className="search-filter-item">
            <label>Team Member</label>
            <Select
              styles={selectStyles}
              options={memberOptions}
              value={selectedMember}
              onChange={setSelectedMember}
              placeholder="All Members"
              isClearable
              menuPortalTarget={document.body}
            />
          </div>
        </div>

        <div className="search-text-row">
          <div className="search-input-wrap">
            <HiOutlineSearch size={18} />
            <input
              type="text"
              placeholder="Type to search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {hasFilters && (
            <button className="search-clear-btn" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {liveLoading && (
        <div className="search-loading">
          <div className="loading-bar"><div className="loading-bar-inner" /></div>
          <span>Fetching live GCP resources...</span>
        </div>
      )}

      <div className="search-results-header">
        <span className="search-results-count">
          {totalCount} result{totalCount !== 1 ? 's' : ''} found
        </span>
        {selectedProject && !liveLoading && (
          <button
            className="search-refresh-btn"
            onClick={() => fetchLiveResources(selectedProject.value)}
            title="Refresh live data"
          >
            <HiOutlineRefresh size={16} /> Refresh
          </button>
        )}
      </div>

      <div className="search-results">
        {orderedSections.map(sectionKey => {
          const items = filteredResults[sectionKey];
          if (!items?.length) return null;

          if (sectionKey === 'gcp_projects') {
            return (
              <ResultSection
                key={sectionKey}
                title="GCP Projects"
                count={items.length}
                icon={HiOutlineCloud}
                color="var(--accent-blue)"
                expanded={isSectionExpanded(sectionKey)}
                onToggle={() => toggleSection(sectionKey)}
              >
                {items.map(p => (
                  <div key={p._id} className="search-result-card" onClick={() => navigate('/gcp-projects')}>
                    <HiOutlineCloud size={16} style={{ color: 'var(--accent-blue)' }} />
                    <div className="search-result-info">
                      <span className="search-result-name">{p.projectName}</span>
                      <span className="search-result-sub">{p.projectId}</span>
                    </div>
                    <div className="search-result-badges">
                      <span className={`search-badge ${p.authMode === 'adc' ? 'badge-green' : 'badge-blue'}`}>
                        {p.authMode === 'adc' ? 'ADC' : 'SA KEY'}
                      </span>
                      <span className={`search-badge ${p.status === 'connected' ? 'badge-green' : 'badge-red'}`}>
                        {p.status}
                      </span>
                    </div>
                  </div>
                ))}
              </ResultSection>
            );
          }

          if (sectionKey === 'team_members') {
            return (
              <ResultSection
                key={sectionKey}
                title="Team Members"
                count={items.length}
                icon={HiOutlineUserGroup}
                color="var(--accent-purple, #a855f7)"
                expanded={isSectionExpanded(sectionKey)}
                onToggle={() => toggleSection(sectionKey)}
              >
                {items.map(m => {
                  const typeCounts = {};
                  for (const s of (m.services || [])) {
                    typeCounts[s.type] = (typeCounts[s.type] || 0) + 1;
                  }
                  return (
                    <div key={m._id} className="search-result-card" onClick={() => navigate(`/team/${m._id}`)}>
                      <HiOutlineUserGroup size={16} style={{ color: 'var(--accent-purple, #a855f7)' }} />
                      <div className="search-result-info">
                        <span className="search-result-name">{m.name}</span>
                        <span className="search-result-sub">{m.services?.length || 0} services</span>
                      </div>
                      <div className="search-result-chips">
                        {Object.entries(typeCounts).slice(0, 4).map(([type, count]) => {
                          const meta = TYPE_META[type];
                          return meta ? (
                            <span key={type} className="search-chip" style={{ borderColor: meta.color, color: meta.color }}>
                              {meta.label.split(' ')[0]} ({count})
                            </span>
                          ) : null;
                        })}
                        {Object.keys(typeCounts).length > 4 && (
                          <span className="search-chip">+{Object.keys(typeCounts).length - 4} more</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </ResultSection>
            );
          }

          if (sectionKey === 'products') {
            return (
              <ResultSection
                key={sectionKey}
                title="Products"
                count={items.length}
                icon={HiOutlineCube}
                color="var(--accent-orange, #f97316)"
                expanded={isSectionExpanded(sectionKey)}
                onToggle={() => toggleSection(sectionKey)}
              >
                {items.map(p => (
                  <div key={p._id} className="search-result-card" onClick={() => navigate(`/products/${p._id}`)}>
                    <HiOutlineCube size={16} style={{ color: 'var(--accent-orange, #f97316)' }} />
                    <div className="search-result-info">
                      <span className="search-result-name">{p.name}</span>
                      <span className="search-result-sub">{p.cloudRuns?.length || 0} Cloud Run services</span>
                    </div>
                    <div className="search-result-chips">
                      {(p.cloudRuns || []).slice(0, 3).map((cr, i) => (
                        <span key={i} className="search-chip">{cr.serviceName}</span>
                      ))}
                      {(p.cloudRuns || []).length > 3 && (
                        <span className="search-chip">+{p.cloudRuns.length - 3} more</span>
                      )}
                    </div>
                  </div>
                ))}
              </ResultSection>
            );
          }

          if (sectionKey.startsWith('service_')) {
            const type = sectionKey.replace('service_', '');
            const meta = TYPE_META[type] || { label: type, icon: HiOutlineCog, color: 'var(--text-muted)' };
            const Icon = meta.icon;

            return (
              <ResultSection
                key={sectionKey}
                title={meta.label}
                count={items.length}
                icon={Icon}
                color={meta.color}
                expanded={isSectionExpanded(sectionKey)}
                onToggle={() => toggleSection(sectionKey)}
              >
                {items.map((svc, idx) => {
                  const owners = svc._memberName
                    ? [{ _id: svc._memberId, name: svc._memberName }]
                    : getOwners(type, svc.name, svc.gcpProjectId);
                  const inProducts = type === 'cloud_run' ? getProducts(svc.name, svc.gcpProjectId) : [];
                  const isAssigned = owners.length > 0 || inProducts.length > 0;

                  return (
                    <div
                      key={`${svc.name}-${svc.gcpProjectId}-${idx}`}
                      className="search-result-card"
                      onClick={() => {
                        if (owners.length === 1) navigate(`/team/${owners[0]._id}`);
                        else if (inProducts.length === 1) navigate(`/products/${inProducts[0]._id}`);
                        else navigate(SERVICE_ROUTE_MAP[type] || '/');
                      }}
                    >
                      <Icon size={16} style={{ color: meta.color }} />
                      <div className="search-result-info">
                        <span className="search-result-name">{svc.name}</span>
                        <span className="search-result-sub">
                          {svc.region && `${svc.region} · `}
                          {svc.gcpProjectName || ''}
                          {svc.status && ` · ${svc.status}`}
                        </span>
                      </div>
                      <div className="search-result-badges">
                        {owners.map(o => (
                          <span
                            key={o._id}
                            className="search-badge badge-purple"
                            onClick={e => { e.stopPropagation(); navigate(`/team/${o._id}`); }}
                          >
                            {o.name}
                          </span>
                        ))}
                        {inProducts.map(p => (
                          <span
                            key={p._id}
                            className="search-badge badge-orange"
                            onClick={e => { e.stopPropagation(); navigate(`/products/${p._id}`); }}
                          >
                            {p.name}
                          </span>
                        ))}
                        {!isAssigned && <span className="search-badge badge-gray">Unassigned</span>}
                      </div>
                    </div>
                  );
                })}
              </ResultSection>
            );
          }

          return null;
        })}

        {!liveLoading && totalCount === 0 && (
          <div className="search-empty">
            <HiOutlineSearch size={40} />
            <h3>No results found</h3>
            <p>
              {hasFilters
                ? 'Try adjusting your filters or search term'
                : 'Select a project to load live GCP resources, or type to search existing data'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultSection({ title, count, icon: Icon, color, expanded, onToggle, children }) {
  return (
    <div className="search-section">
      <div className="search-section-header" onClick={onToggle}>
        <div className="search-section-left">
          <Icon size={18} style={{ color }} />
          <span className="search-section-title">{title}</span>
          <span className="search-section-count">{count}</span>
        </div>
        <HiOutlineChevronDown
          size={16}
          className={`search-chevron ${expanded !== false ? 'expanded' : ''}`}
        />
      </div>
      {expanded !== false && (
        <div className="search-section-body">
          {children}
        </div>
      )}
    </div>
  );
}

export default Search;
